import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { LEAD_MANAGEMENT_HOME_CRUMB } from '@components/breadcrumbs/Breadcrumbs.config';
import { Breadcrumbs } from '@components/index';
import { LEAD_MANAGEMENT } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  sendMixpanelEvent,
  trackPageView
} from '@core/analytics/mixpanel';
import useNotify from '@hooks/notify';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';

import ContactEditModal from '../components/ContactEditModal';
import LeadActivityPanel from '../components/LeadActivityPanel';
import LeadEditModal from '../components/LeadEditModal';
import LeadHeader from '../components/LeadHeader';
import LeadInformation from '../components/LeadInformation';
import LeadsAddTagModal from '../components/LeadsAddTagModal';
import MoveToCofeeModal from '../components/move-to-cofee/MoveToCofeeModal';
import { StepStatus } from '../constants';
import useLeadAgentRestrictions from '../hooks/useLeadAgentRestrictions';
import useLeadSources from '../hooks/useLeadSources';
import {
  useGetAgentsQuery,
  useGetLeadDetailsQuery,
  useGetStatusQuery,
  useGetTagsQuery,
  useUpdateLeadMutation
} from '../queries/LeadManagementQuery';
import { LeadType } from '../types/common';
import { computeObjectDifference } from '../utils/leadAnalytics';
import getSaveLeadErrorMessage from '../utils/leadApiErrorMessages';
import LeadDetailsEmptyState from './LeadDetailsEmptyState';

import { DropdownOption } from '../../../types/dropdown';
import {
  LeadTag,
  StatusEntity,
  StatusOption,
  Step,
  Tag
} from '../../../types/lead-management';

const LeadDetails = () => {
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { leadId } = useParams();
  const { currentOrg } = useSelector((state: any) => state.user);

  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const {
    data: leadDetailsResponse,
    isLoading,
    error,
    refetch
  } = useGetLeadDetailsQuery(
    {
      organizationId: organizationId || '',
      leadId: leadId || ''
    },
    {
      skip: !organizationId || !leadId,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: statusResponse } = useGetStatusQuery(
    {
      organizationId: organizationId || ''
    },
    {
      skip: !organizationId
    }
  );

  const { data: tagsResponse, refetch: refetchTags } = useGetTagsQuery(
    {
      organizationId: organizationId || ''
    },
    {
      skip: !organizationId
    }
  );

  const [updateLead, { isLoading: isUpdating }] = useUpdateLeadMutation();
  const { getSourceId } = useLeadSources();
  const { data: agentsData } = useGetAgentsQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId }
  );

  const { isSelfAssignEnabled, getAssigneeAccess } = useLeadAgentRestrictions();

  const [selectedPriority, setSelectedPriority] = useState<string>('Low');
  const [isTagModalOpen, setIsTagModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState<boolean>(false);
  const [isMoveToCofeeOpen, setIsMoveToCofeeOpen] = useState<boolean>(false);
  const [isMovedToCofee, setIsMovedToCofee] = useState<boolean>(false);
  const [activityReloadKey, setActivityReloadKey] = useState<number>(0);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState<boolean>(false);
  const [apiTagOptions, setApiTagOptions] = useState<Tag[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [stepperSteps, setStepperSteps] = useState<Step[]>([]);
  const [updatingStepId, setUpdatingStepId] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [isUpdatingAssignee, setIsUpdatingAssignee] = useState<boolean>(false);
  const [assigneeOptions, setAssigneeOptions] = useState<DropdownOption[]>([]);

  const leadData = leadDetailsResponse;

  const statusOptions = useMemo(
    () =>
      statusResponse?.map((status: StatusEntity) => ({
        id: status?.id,
        label: status?.value,
        color: status?.color,
        value: status?.id
      })) || [],
    [statusResponse]
  );

  const getStepperSteps = useCallback(
    (status: LeadType['status']) => {
      if (statusOptions.length === 0) {
        return [];
      }

      const allSteps = statusOptions.map((statusOption: StatusOption) => ({
        id: statusOption.id,
        label: statusOption.label
      }));

      const statusIndexMap: Record<string, number> = {};

      statusOptions.forEach((statusOption: StatusOption, index: number) => {
        statusIndexMap[statusOption.id] = index;
        statusIndexMap[statusOption.label?.toLowerCase()] = index;
        statusIndexMap[statusOption.label] = index;
      });

      const currentIndex =
        statusIndexMap[status?.value?.toLowerCase()] ||
        statusIndexMap[status?.value] ||
        0;

      return allSteps.map((step: StatusOption, index: number) => {
        let stepStatus: StepStatus;
        if (index < currentIndex) {
          stepStatus = StepStatus.COMPLETED;
        } else if (index === currentIndex) {
          stepStatus = StepStatus.CURRENT;
        } else {
          stepStatus = StepStatus.PENDING;
        }

        return {
          ...step,
          status: stepStatus,
          actualStatus: step.label
        };
      });
    },
    [statusOptions]
  );

  const handleLeadUpdate = async (updates: {
    statusId?: string;
    priority?: string;
    assigneeId?: string;
    stepIndex?: number;
    stepId?: string;
    modalData?: Partial<LeadType>;
    updateType?: 'status' | 'priority' | 'assignee' | 'edit' | 'contact';
  }): Promise<boolean> => {
    if (!leadData || !organizationId || !leadId) {
      return false;
    }

    const {
      statusId,
      priority,
      assigneeId,
      stepIndex,
      stepId,
      modalData,
      updateType
    } = updates;

    try {
      if (statusId) {
        setUpdatingStepId(stepId || '');
      }
      if (priority) {
        setIsUpdatingPriority(true);
      }
      if (assigneeId) {
        setIsUpdatingAssignee(true);
      }

      let targetStepIndex: number | undefined;
      let targetStep: Step | undefined;

      if (statusId && (stepIndex !== undefined || stepId)) {
        if (stepIndex !== undefined) {
          targetStepIndex = stepIndex;
          targetStep = stepperSteps[stepIndex];
        } else if (stepId) {
          targetStepIndex = stepperSteps.findIndex(
            (step) => step.id === stepId
          );
          targetStep = stepperSteps[targetStepIndex];
        }

        if (targetStepIndex === -1 || !targetStep) {
          throw new Error('Invalid step');
        }
      }

      const createUpdatePayload = () => {
        const basePayload = {
          name: leadData?.name || '',
          email_id: leadData?.email_id || '',
          country_code: leadData?.country_code || '',
          mobile_number: leadData?.mobile_number || '',
          secondary_mobile_number: leadData?.secondary_mobile_number || null,
          secondary_country_code: leadData?.secondary_mobile_number
            ? leadData?.secondary_country_code || ''
            : null,
          country: leadData?.country ?? null,
          state: leadData?.state ?? null,
          city: leadData?.city ?? null,
          pincode: leadData?.pincode ?? null,
          category_id: leadData?.category?.id || null,
          source_id: getSourceId(leadData?.source) || null,
          assignee_id: leadData?.assignee?.id || null,
          status: leadData?.status?.id || '',
          deal_value: leadData?.deal_value ?? 0,
          priority: leadData?.priority || null,
          description: leadData?.description?.trim() || null,
          tags: leadData?.tags?.map((tag: LeadTag) => tag?.id) || [],
          documents: leadData?.documents || []
        };

        if (statusId) {
          basePayload.status = statusId;
        }

        if (priority) {
          basePayload.priority = priority;
        }

        if (assigneeId !== undefined) {
          basePayload.assignee_id =
            assigneeId === 'unassigned' ? null : assigneeId;
        }

        if (modalData) {
          if (modalData.name !== undefined) {
            basePayload.name = modalData.name || '';
          }
          if (modalData.email_id !== undefined) {
            basePayload.email_id = modalData.email_id || '';
          }
          if (modalData.country_code !== undefined) {
            basePayload.country_code = modalData.country_code || '';
          }
          if (modalData.mobile_number !== undefined) {
            basePayload.mobile_number = modalData.mobile_number || '';
          }
          if (modalData.secondary_mobile_number !== undefined) {
            basePayload.secondary_mobile_number =
              modalData.secondary_mobile_number || '';
            basePayload.secondary_country_code =
              modalData.secondary_mobile_number
                ? modalData.secondary_country_code ||
                  leadData?.secondary_country_code ||
                  ''
                : null;
          }
          if (modalData.country !== undefined) {
            basePayload.country = modalData.country || null;
          }
          if (modalData.state !== undefined) {
            basePayload.state = modalData.state || '';
          }
          if (modalData.city !== undefined) {
            basePayload.city = modalData.city || '';
          }
          if (modalData.pincode !== undefined) {
            basePayload.pincode = modalData.pincode || null;
          }
          if (modalData.category?.id !== undefined) {
            basePayload.category_id = modalData.category.id || null;
          }
          if (modalData.source !== undefined) {
            basePayload.source_id = getSourceId(modalData.source) || null;
          }
          if (modalData.assignee !== undefined) {
            basePayload.assignee_id =
              modalData.assignee?.id === 'unassigned'
                ? null
                : modalData.assignee?.id || null;
          }
          if (modalData.status !== undefined) {
            basePayload.status = modalData.status?.id || '';
          }
          if (modalData.deal_value !== undefined) {
            basePayload.deal_value = modalData.deal_value * 100;
          }
          if (modalData.priority !== undefined) {
            basePayload.priority = modalData.priority || null;
          }
          if (modalData.description !== undefined) {
            basePayload.description = modalData.description?.trim() || null;
          }
          if (modalData.tags !== undefined) {
            basePayload.tags =
              modalData.tags.map((tag: LeadTag) => tag?.id) || [];
          }
        }

        return basePayload;
      };

      const updatePayload = createUpdatePayload();

      await updateLead({
        organizationId,
        leadId,
        body: updatePayload
      }).unwrap();

      if (statusId && targetStepIndex !== undefined) {
        setCurrentStepIndex(targetStepIndex);
        setStepperSteps((prevSteps) =>
          prevSteps.map((step: Step, index: number) => {
            let stepStatus: StepStatus;
            if (index < targetStepIndex!) {
              stepStatus = StepStatus.COMPLETED;
            } else if (index === targetStepIndex!) {
              stepStatus = StepStatus.CURRENT;
            } else {
              stepStatus = StepStatus.PENDING;
            }

            return {
              ...step,
              status: stepStatus
            };
          })
        );
      }

      if (priority) {
        setSelectedPriority(priority);
      }

      if (assigneeId) {
        setSelectedAssignee(assigneeId);
      }

      if (updateType === 'edit') {
        setIsEditModalOpen(false);
        const eventPayload = computeObjectDifference(leadData, modalData ?? {});
        sendMixpanelEvent(Event.LEAD_UPDATED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          lead_id: leadId,
          ...eventPayload
        });
      }
      if (updateType === 'contact') {
        setIsContactModalOpen(false);
      }

      let successMessage = '';
      if (statusId && targetStep) {
        successMessage = `Lead status updated to ${targetStep.label} successfully`;
      } else if (priority) {
        const formattedPriority =
          priority.charAt(0).toUpperCase() + priority.slice(1);
        successMessage = `Lead priority updated to ${formattedPriority} successfully`;
      } else if (assigneeId) {
        const assigneeName =
          assigneeOptions.find((opt) => opt?.value === assigneeId)?.label ||
          'Unassigned';
        successMessage = `Lead assigned to ${assigneeName} successfully`;
      } else if (updateType === 'edit') {
        successMessage = 'Lead information updated successfully';
      } else if (updateType === 'contact') {
        successMessage = 'Contact information updated successfully';
      } else {
        successMessage = 'Lead updated successfully';
      }

      createSuccessAlert(successMessage, { classOverride: 'max-w-xl' });
      if (updateType === 'assignee') {
        sendMixpanelEvent(Event.LEAD_ASSIGNED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          lead_id: leadId,
          assignee_id: assigneeId === 'unassigned' ? null : assigneeId,
          assignment_method: 'manual',
          previous_assignee: leadData?.assignee?.id || null,
          is_reassignment: !!leadData?.assignee?.id
        });
      }
      refetch();
      return true;
    } catch (err: unknown) {
      const isSelfClaim =
        updateType === 'assignee' &&
        isSelfAssignEnabled &&
        !leadData?.assignee?.id;
      const assigneeContext = isSelfClaim ? 'selfAssign' : 'assignee';
      createErrorAlert(
        getSaveLeadErrorMessage(err, true, {
          updateContext: updateType === 'assignee' ? assigneeContext : undefined
        })
      );
      if (isSelfClaim) refetch();
      return false;
    } finally {
      setUpdatingStepId(null);
      setIsUpdatingPriority(false);
      setIsUpdatingAssignee(false);
    }
  };

  const handleStatusUpdate = async (
    statusId: string,
    stepIndex?: number,
    stepId?: string
  ) => {
    await handleLeadUpdate({
      statusId,
      stepIndex,
      stepId,
      updateType: 'status'
    });
  };

  const handlePriorityChange = async (priority: string) => {
    await handleLeadUpdate({ priority, updateType: 'priority' });
  };

  const handleAssigneeChange = async (assigneeId: string) => {
    await handleLeadUpdate({ assigneeId, updateType: 'assignee' });
  };

  const handleEditModalSave = async (updatedData: Partial<LeadType>) => {
    await handleLeadUpdate({ modalData: updatedData, updateType: 'edit' });
  };

  const handleContactSave = async (updatedData: Partial<LeadType>) => {
    await handleLeadUpdate({ modalData: updatedData, updateType: 'contact' });
  };

  const handleStepClick = async (stepIndex: number) => {
    const clickedStep = stepperSteps[stepIndex];
    if (clickedStep) {
      await handleStatusUpdate(clickedStep.id, stepIndex);
    }
  };

  const handleStatusChange = async (stepId: string, statusId: string) => {
    await handleStatusUpdate(statusId, undefined, stepId);
  };

  const handleEditInformation = () => {
    setIsEditModalOpen(true);
  };

  const handleEditContact = () => {
    setIsContactModalOpen(true);
  };

  const handleAddTags = () => {
    setIsTagModalOpen(true);
  };

  // Mixpanel event for lead details viewed
  const hasTrackedLeadDetails = useRef(false);
  useEffect(() => {
    const hasData = leadData && Object.keys(leadData).length > 0;
    if (!hasData || hasTrackedLeadDetails.current) return;
    hasTrackedLeadDetails.current = true;

    trackPageView('Lead details page viewed');
    sendMixpanelEvent(Event.LEAD_DETAILS_VIEWED, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      lead_id: leadData.id,
      lead_status: leadData.status?.value,
      priority: leadData.priority,
      source: leadData.source,
      lead_category: leadData.category?.value,
      lead_location: `${leadData.country}, ${leadData.state}, ${leadData.city}`,
      lead_phone_number: leadData.mobile_number,
      lead_email: leadData.email_id,
      has_alternate_number: !!leadData.secondary_mobile_number
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadData]);

  useEffect(() => {
    if (leadData) {
      setSelectedPriority(leadData.priority?.toLowerCase());
    }
  }, [leadData]);

  useEffect(() => {
    if (leadData) {
      const steps = getStepperSteps(leadData.status);
      const currentIndex = steps.findIndex(
        (step: Step) => step.status === StepStatus.CURRENT
      );
      setStepperSteps(steps);
      setCurrentStepIndex(currentIndex !== -1 ? currentIndex : 0);
    }
  }, [leadData, statusOptions, getStepperSteps]);

  useEffect(() => {
    if (Array.isArray(tagsResponse) && tagsResponse.length > 0) {
      const transformedTags: Tag[] = tagsResponse.map((tag: LeadTag) => ({
        id: tag.id,
        name: tag.name,
        color: tag.color || '#F2E1E9'
      }));
      setApiTagOptions(transformedTags);
    }
  }, [tagsResponse]);

  useEffect(() => {
    const unassignedOption = {
      id: 'unassigned',
      label: 'Unassigned',
      value: 'unassigned'
    };
    const transformedAgents =
      agentsData?.sales_agents?.map(
        (agent: { user_id: string; name: string }) => ({
          id: agent.user_id,
          label: agent.name,
          value: agent.user_id
        })
      ) || [];

    const options = [unassignedOption, ...transformedAgents];

    const currentAssigneeId = leadData?.assignee?.id;
    const currentAssigneeName = leadData?.assignee?.name;
    const isKnownAssignee = options.some(
      (option) => option.id === currentAssigneeId
    );

    if (currentAssigneeId && currentAssigneeName && !isKnownAssignee) {
      options.push({
        id: currentAssigneeId,
        label: currentAssigneeName,
        value: currentAssigneeId
      });
    }

    setAssigneeOptions(options);
  }, [agentsData, leadData]);

  useEffect(() => {
    if (leadData) {
      setSelectedAssignee(leadData.assignee?.id || 'unassigned');
    }
  }, [leadData]);

  const onAddTags = async (tags: Tag[]) => {
    try {
      const uniqueTags = tags.filter(
        (tag, index, self) => index === self.findIndex((t) => t.id === tag.id)
      );

      await handleLeadUpdate({
        modalData: {
          tags: uniqueTags
        },
        updateType: 'edit'
      });

      createSuccessAlert('Tags added to lead successfully');
      setIsTagModalOpen(false);
      refetchTags();
    } catch (tagsError) {
      createErrorAlert('Failed to add tags to lead. Please try again.');
    }
  };

  if (!leadData) {
    return <LeadDetailsEmptyState isLoading={isLoading} error={error} />;
  }

  const assigneeAccess = getAssigneeAccess(
    assigneeOptions,
    leadData.assignee?.id
  );

  return (
    <div className="page -mb-21 bg-bgBase">
      <Head title="Lead Details" />
      <Breadcrumbs
        dynamicCrumbs={[
          LEAD_MANAGEMENT_HOME_CRUMB,
          {
            name: 'Lead Details',
            path: `${LEAD_MANAGEMENT.LEAD_DETAILS.replace(
              ':leadId',
              leadId || ''
            )}`
          }
        ]}
      />
      <LeadHeader
        leadData={leadData}
        isOnboarded={Boolean(leadData.is_onboarded) || isMovedToCofee}
        selectedPriority={selectedPriority}
        isUpdatingPriority={isUpdatingPriority}
        isUpdating={isUpdating}
        stepperSteps={stepperSteps}
        currentStepIndex={currentStepIndex}
        statusOptions={statusOptions}
        updatingStepId={updatingStepId}
        selectedAssignee={selectedAssignee}
        isUpdatingAssignee={isUpdatingAssignee}
        assigneeOptions={assigneeAccess.options}
        onPriorityChange={handlePriorityChange}
        onEditInformation={handleEditInformation}
        onMoveToCofee={() => setIsMoveToCofeeOpen(true)}
        onStepClick={handleStepClick}
        onStatusChange={handleStatusChange}
        onAddTags={handleAddTags}
        onAssigneeChange={handleAssigneeChange}
        isAssigneeDisabled={assigneeAccess.isDisabled}
      />

      <div className="mt-6 flex gap-6">
        <LeadInformation
          handleEditContact={handleEditContact}
          leadData={leadData}
        />

        <LeadActivityPanel
          leadId={leadData?.id}
          analyticsData={{
            leadDetails: {
              leadPhone: leadData?.mobile_number,
              leadName: leadData?.name
            }
          }}
          reloadKey={activityReloadKey}
        />
      </div>

      {isContactModalOpen && (
        <ContactEditModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          leadData={leadData}
          onSave={handleContactSave}
          isSaving={isUpdating}
        />
      )}
      {isEditModalOpen && (
        <LeadEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          leadData={leadData}
          statusOptions={statusOptions}
          assigneeOptions={assigneeAccess.options}
          onSave={handleEditModalSave}
          isSaving={isUpdating}
          onTagRenameSuccess={refetch}
          isAssigneeDisabled={assigneeAccess.isDisabled}
        />
      )}
      {isTagModalOpen && (
        <LeadsAddTagModal
          isOpen={isTagModalOpen}
          onClose={() => setIsTagModalOpen(false)}
          onAddTags={onAddTags}
          existingTags={leadData.tags}
          tagsList={apiTagOptions}
          isBulkEdit={false}
          existingSelectedTags={leadData.tags?.map((tag: LeadTag) => ({
            id: tag.id,
            name: tag.name,
            color: tag.color || '#F2E1E9'
          }))}
          onTagRenameSuccess={refetch}
        />
      )}
      {isMoveToCofeeOpen && (
        <MoveToCofeeModal
          onClose={() => setIsMoveToCofeeOpen(false)}
          onSuccess={() => {
            setIsMovedToCofee(true);
            setActivityReloadKey((prev) => prev + 1);
          }}
          leadData={leadData}
          onUpdateLeadPhone={(mobileNumber, countryCode) =>
            handleLeadUpdate({
              modalData: {
                mobile_number: mobileNumber,
                country_code: countryCode
              },
              updateType: 'contact'
            })
          }
        />
      )}
    </div>
  );
};

export default LeadDetails;
